import { Target, CheckCircle2, Circle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { PreviewPaneHeader } from '@/components/features/preview-pane-header';
import type { SidePanelView } from './product-board-types';
import { mockData } from './product-board.utils';

export type ProductBoardCycleDetailProps = {
  selectedBetId: string | null;
  completedTasks: Set<string>;
  handleTaskToggle: (taskId: string, taskText: string) => void;
  sidePanelView: SidePanelView;
  handleOpenInEditor: () => void;
  handleCloseSidePanel: () => void;
};

export function ProductBoardCycleDetail({
  selectedBetId,
  completedTasks,
  handleTaskToggle,
  sidePanelView,
  handleOpenInEditor,
  handleCloseSidePanel,
}: ProductBoardCycleDetailProps) {
  const cycle = mockData.currentCycle;
  const selectedBet = selectedBetId
    ? cycle.bigBets.find((b) => b.id === selectedBetId)
    : cycle.bigBets[0];

  if (!selectedBet) {
    return <div>Bet not found</div>;
  }

  const TaskRow = ({ taskKey, label, done = true }: { taskKey: string; label: string; done?: boolean }) => {
    const isChecked = done || completedTasks.has(taskKey);
    return (
      <div
        className="flex items-start gap-2.5 py-1.5 -mx-1 px-1 rounded hover:bg-muted/40 cursor-pointer transition-colors group"
        onClick={() => handleTaskToggle(taskKey, label)}
        title="Click to toggle"
      >
        {isChecked ? (
          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
        ) : (
          <Circle className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 mt-0.5" />
        )}
        <span className={`text-sm ${isChecked ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
          {label}
        </span>
      </div>
    );
  };

  const content = (
    <div className="px-6 py-5 space-y-0">
      {sidePanelView !== 'cycle-detail' && (
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Cycle {cycle.number}</p>
      )}
      <div className="flex items-start gap-3 mb-1">
        <h2 className="text-lg font-semibold leading-snug flex-1">{selectedBet.title}</h2>
        <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
          <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/10 text-purple-500 border border-purple-500/20">
            Big Bet
          </span>
          <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/10 text-green-600 border border-green-500/20">
            Complete
          </span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        Appetite: {selectedBet.appetite} � Owner: {selectedBet.owner}
      </p>

      <div className="mb-6">
        <div className="flex justify-between items-baseline mb-1.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Progress</span>
          <span className="text-sm font-semibold tabular-nums">{selectedBet.progress}%</span>
        </div>
        <Progress value={selectedBet.progress} className="h-1.5" />
      </div>

      <div className="border-t border-border" />

      <div className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Must-Haves</span>
          <span className="text-xs text-muted-foreground">
            {selectedBet.mustHaves.completed}/{selectedBet.mustHaves.total} complete
          </span>
        </div>
        <div className="space-y-0.5">
          <TaskRow taskKey="fix-files" label="Fix incorrectly dated files" />
          <TaskRow taskKey="archive-specs" label="Archive completed specs" />
          <TaskRow taskKey="create-index" label="Create CANONICAL-INDEX.md" />
          <TaskRow taskKey="design-system" label="Design hybrid product management system" />
          <TaskRow taskKey="create-structure" label="Create product folder structure" />
          <TaskRow taskKey="create-templates" label="Create templates (cycle, bet, experiment, roadmap)" />
          <TaskRow taskKey="init-cycle" label="Initialize first real cycle plan" done={false} />
          <TaskRow taskKey="populate-roadmaps" label="Populate NOW/NEXT/LATER roadmaps with actual work" />
          <TaskRow taskKey="create-guide" label="Create product management workflow guide" />
          <TaskRow taskKey="trial-run" label="Trial run with first cycle" />
        </div>
      </div>

      <div className="border-t border-border" />

      <div className="pt-5 pb-4">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Success Criteria</span>
        <div className="space-y-0.5 mt-3">
          <div className="flex items-start gap-2.5 py-1.5">
            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
            <span className="text-sm text-muted-foreground line-through">Clear documentation structure</span>
          </div>
          <div className="flex items-start gap-2.5 py-1.5">
            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
            <span className="text-sm text-muted-foreground line-through">Reduced root specs from 234 ? 169 files</span>
          </div>
          <div className="flex items-start gap-2.5 py-1.5">
            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
            <span className="text-sm text-muted-foreground line-through">Templates ready to use</span>
          </div>
          <div
            className="flex items-start gap-2.5 py-1.5 -mx-1 px-1 rounded hover:bg-blue-500/5 cursor-pointer transition-colors"
            onClick={() => toast('In progress', { description: 'Being validated in current cycle' })}
          >
            <Circle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
            <span className="text-sm text-blue-500">System validated through first real cycle (in progress)</span>
          </div>
        </div>
      </div>

      <div className="border-t border-border" />

      <div className="pt-5">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Progress Notes</span>
        <div className="mt-3 pl-3 border-l-2 border-border space-y-1">
          <p className="text-xs font-medium text-foreground">Day 0 (Jan 3)</p>
          <p className="text-sm text-muted-foreground">System designed and implemented</p>
          <ul className="text-sm text-muted-foreground space-y-0.5 list-disc list-inside mt-1">
            <li>Fixed dated files, archived 65 specs</li>
            <li>Created complete product management structure</li>
            <li>Templates and roadmaps ready</li>
          </ul>
        </div>
      </div>
    </div>
  );

  if (sidePanelView === 'cycle-detail') {
    return (
      <div className="h-full flex flex-col bg-card">
        <PreviewPaneHeader
          row
          icon={<Target className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
          label={`Cycle ${cycle.number}`}
          onOpen={handleOpenInEditor}
          onClose={handleCloseSidePanel}
        />

        <div className="flex-1 overflow-y-auto px-4 py-4">{content}</div>
      </div>
    );
  }

  return content;
}
